import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useLanguages } from '../../hooks/useLanguages';
import { loadTopWords } from '../../services/topWordsV2';

// The target language's most frequently used words, paired with their
// meaning in the learner's native language — see topWordsV2.js. Mirrors the
// mobile app's TopWordsScreen (lib/features/top_words/presentation/top_words_screen.dart).
export default function TopWordsPage() {
  const { t } = useTranslation();
  const { languagePrefs, openLanguagePreferences } = useAuth();
  const { languages } = useLanguages();
  const targetCode = languagePrefs?.targetLanguage || 'en';
  const nativeCode = languagePrefs?.nativeLanguage || 'en';
  const targetLang = languages.find((l) => l.code === targetCode);
  const targetLabel = targetLang?.label || targetCode;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [entries, setEntries] = useState([]);

  const synthRef = useRef(
    typeof window !== 'undefined' && 'speechSynthesis' in window ? window.speechSynthesis : null,
  );

  function speak(text) {
    if (!synthRef.current || !text) return;
    try {
      synthRef.current.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = targetLang?.tts || targetCode || 'en';
      utterance.rate = 0.95;
      utterance.onerror = () => {};
      synthRef.current.speak(utterance);
    } catch {
      // Never let a speak button crash the page.
    }
  }

  useEffect(() => () => { synthRef.current?.cancel(); }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    loadTopWords(targetCode, nativeCode)
      .then((data) => { if (active) { setEntries(data); setLoading(false); } })
      .catch(() => { if (active) { setError(true); setLoading(false); } });
    return () => { active = false; };
  }, [targetCode, nativeCode]);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${t('topWordsPage.title')} | SublingoX`;
    return () => { document.title = previousTitle; };
  }, [t]);

  function retry() {
    setLoading(true);
    setError(false);
    loadTopWords(targetCode, nativeCode, { force: true })
      .then((data) => { setEntries(data); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
  }

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-8 sm:px-6 lg:px-8">
      <main className="mx-auto max-w-2xl">
        <header className="mb-6 flex items-center justify-between gap-3">
          <h1 className="text-2xl font-black text-slate-900 sm:text-3xl">{t('topWordsPage.title')}</h1>
          <button
            type="button"
            onClick={openLanguagePreferences}
            className="shrink-0 rounded-xl border border-indigo-200 bg-white px-3.5 py-2 text-xs font-bold text-indigo-700 hover:bg-indigo-50"
          >
            {targetLang?.flag ? `${targetLang.flag} ` : ''}{targetLabel}
          </button>
        </header>

        {loading && (
          <div className="space-y-2">
            {[1, 2, 3, 4, 5, 6].map((item) => <div key={item} className="h-16 animate-pulse rounded-2xl bg-slate-100" />)}
          </div>
        )}

        {!loading && error && (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
            <span className="text-5xl">📖</span>
            <p className="mt-3 text-sm font-semibold text-slate-600">{t('topWordsPage.notAvailableTitle', { lang: targetLabel })}</p>
            <button type="button" onClick={retry} className="mt-4 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white hover:bg-indigo-700">
              {t('topWordsPage.retry')}
            </button>
          </div>
        )}

        {!loading && !error && entries.length === 0 && (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
            <span className="text-5xl">📖</span>
            <p className="mt-3 text-sm font-bold text-slate-700">{t('topWordsPage.notAvailableTitle', { lang: targetLabel })}</p>
            <p className="mt-1.5 max-w-xs text-sm text-slate-500">{t('topWordsPage.notAvailableDesc')}</p>
          </div>
        )}

        {!loading && !error && entries.length > 0 && (
          <>
            <h2 className="text-lg font-black text-slate-900">{t('topWordsPage.titleWithCount', { count: entries.length })}</h2>
            <p className="mb-4 mt-1 text-sm text-slate-600">{t('topWordsPage.desc')}</p>
            <div className="space-y-2">
              {entries.map((entry) => (
                <div key={entry.id} className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-white px-3.5 py-3 shadow-sm">
                  <span className="w-6 shrink-0 text-center text-xs font-bold text-slate-400">{entry.order}</span>
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-indigo-50 text-lg">
                    {entry.emoji || '📖'}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-base font-extrabold text-slate-900">{entry.text}</p>
                    {entry.meaning && <p className="truncate text-sm text-slate-500">{entry.meaning}</p>}
                  </div>
                  <button
                    type="button"
                    onClick={() => speak(entry.text)}
                    aria-label={t('phrasebookPage.listen')}
                    className="shrink-0 rounded-full p-1.5 text-indigo-600 hover:bg-indigo-50"
                  >
                    🔊
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
